import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Calendar, Clock, Users, Settings,
  Plus, Search, Edit2, Trash2, CheckCircle, XCircle,
  TrendingUp, CalendarDays, User, Briefcase,
  ChevronLeft, ChevronRight, Save, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BookingPanel = () => {
  const { websiteId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [stats, setStats] = useState(null);
  const [services, setServices] = useState([]);
  const [operators, setOperators] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [settings, setSettings] = useState({});
  
  // Modal states
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showOperatorModal, setShowOperatorModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Form states
  const [serviceForm, setServiceForm] = useState({
    name: '', description: '', duration: 60, price: '', color: '#8b5cf6', is_active: true
  });
  const [operatorForm, setOperatorForm] = useState({
    name: '', email: '', phone: '', bio: '', services: [], color: '#8b5cf6', is_active: true
  });
  const [bookingForm, setBookingForm] = useState({
    service_id: '', operator_id: '', customer_name: '', customer_email: '', customer_phone: '',
    start_time: '', duration: 60, notes: ''
  });
  
  const token = localStorage.getItem('token');
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const tabs = [
    { id: 'dashboard', name: 'Dashboard', icon: TrendingUp },
    { id: 'schedule', name: 'Schedule', icon: CalendarDays },
    { id: 'bookings', name: 'Bookings', icon: Calendar },
    { id: 'services', name: 'Services', icon: Briefcase },
    { id: 'operators', name: 'Staff', icon: Users },
    { id: 'settings', name: 'Settings', icon: Settings },
  ];

  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    confirmed: 'bg-blue-500/20 text-blue-400',
    completed: 'bg-green-500/20 text-green-400',
    cancelled: 'bg-red-500/20 text-red-400',
    no_show: 'bg-zinc-500/20 text-zinc-400'
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [statsRes, servicesRes, operatorsRes, bookingsRes, settingsRes] = await Promise.all([
        fetch(`${API_URL}/api/websites/${websiteId}/booking-stats`, { headers }),
        fetch(`${API_URL}/api/websites/${websiteId}/services`, { headers }),
        fetch(`${API_URL}/api/websites/${websiteId}/operators`, { headers }),
        fetch(`${API_URL}/api/websites/${websiteId}/bookings`, { headers }),
        fetch(`${API_URL}/api/websites/${websiteId}/booking-settings`, { headers }),
      ]);
      
      if (statsRes.ok) setStats(await statsRes.json());
      if (servicesRes.ok) setServices(await servicesRes.json());
      if (operatorsRes.ok) setOperators(await operatorsRes.json());
      if (bookingsRes.ok) setBookings(await bookingsRes.json());
      if (settingsRes.ok) setSettings(await settingsRes.json());
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load booking data');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteId]);

  // Service CRUD
  const saveService = async () => {
    try {
      const method = editingItem ? 'PUT' : 'POST';
      const url = editingItem 
        ? `${API_URL}/api/websites/${websiteId}/services/${editingItem.id}`
        : `${API_URL}/api/websites/${websiteId}/services`;
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          ...serviceForm,
          duration: parseInt(serviceForm.duration),
          price: parseFloat(serviceForm.price) || 0
        })
      });
      
      if (response.ok) {
        toast.success(editingItem ? 'Service updated!' : 'Service created!');
        setShowServiceModal(false);
        setEditingItem(null);
        setServiceForm({ name: '', description: '', duration: 60, price: '', color: '#8b5cf6', is_active: true });
        fetchAllData();
      }
    } catch (error) {
      toast.error('Failed to save service');
    }
  };

  const deleteService = async (serviceId) => {
    if (!confirm('Delete this service?')) return;
    try {
      await fetch(`${API_URL}/api/websites/${websiteId}/services/${serviceId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Service deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  // Operator CRUD
  const saveOperator = async () => {
    try {
      const method = editingItem ? 'PUT' : 'POST';
      const url = editingItem 
        ? `${API_URL}/api/websites/${websiteId}/operators/${editingItem.id}`
        : `${API_URL}/api/websites/${websiteId}/operators`;
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(operatorForm)
      });
      
      if (response.ok) {
        toast.success(editingItem ? 'Staff updated!' : 'Staff added!');
        setShowOperatorModal(false);
        setEditingItem(null);
        setOperatorForm({ name: '', email: '', phone: '', bio: '', services: [], color: '#8b5cf6', is_active: true });
        fetchAllData();
      }
    } catch (error) {
      toast.error('Failed to save staff');
    }
  };

  const deleteOperator = async (operatorId) => {
    if (!confirm('Delete this staff member?')) return;
    try {
      await fetch(`${API_URL}/api/websites/${websiteId}/operators/${operatorId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Staff deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete staff');
    }
  };

  // Booking CRUD
  const saveBooking = async () => {
    try {
      const startTime = new Date(bookingForm.start_time);
      const endTime = new Date(startTime.getTime() + bookingForm.duration * 60000);
      
      const response = await fetch(`${API_URL}/api/websites/${websiteId}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          ...bookingForm,
          start_time: startTime.toISOString(),
          end_time: endTime.toISOString(),
          status: 'confirmed'
        })
      });
      
      if (response.ok) {
        toast.success('Booking created!');
        setShowBookingModal(false);
        setBookingForm({ service_id: '', operator_id: '', customer_name: '', customer_email: '', customer_phone: '', start_time: '', duration: 60, notes: '' });
        fetchAllData();
      }
    } catch (error) {
      toast.error('Failed to create booking');
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await fetch(`${API_URL}/api/websites/${websiteId}/bookings/${bookingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status })
      });
      toast.success('Booking updated');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  // Save settings
  const saveSettings = async () => {
    try {
      await fetch(`${API_URL}/api/websites/${websiteId}/booking-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(settings)
      });
      toast.success('Settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', { 
      weekday: 'short', month: 'short', day: 'numeric', 
      hour: 'numeric', minute: '2-digit' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl p-6 border border-blue-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-zinc-400 text-sm">Total Bookings</p>
              <p className="text-3xl font-bold text-white mt-1">{stats?.total_bookings || 0}</p>
            </div>
            <Calendar className="w-10 h-10 text-blue-400" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-emerald-500/20 to-green-500/20 rounded-xl p-6 border border-emerald-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-zinc-400 text-sm">Confirmed</p>
              <p className="text-3xl font-bold text-white mt-1">{stats?.confirmed_bookings || 0}</p>
            </div>
            <CheckCircle className="w-10 h-10 text-emerald-400" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-xl p-6 border border-amber-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-zinc-400 text-sm">Today's Bookings</p>
              <p className="text-3xl font-bold text-white mt-1">{stats?.todays_bookings || 0}</p>
            </div>
            <CalendarDays className="w-10 h-10 text-amber-400" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-violet-500/20 to-purple-500/20 rounded-xl p-6 border border-violet-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-zinc-400 text-sm">Staff Members</p>
              <p className="text-3xl font-bold text-white mt-1">{stats?.total_operators || 0}</p>
            </div>
            <Users className="w-10 h-10 text-violet-400" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Recent Bookings */}
        <div className="bg-zinc-900 rounded-xl border border-white/10 p-6">
          <h3 className="text-lg font-semibold mb-4">Recent Bookings</h3>
          <div className="space-y-3">
            {(stats?.recent_bookings || []).slice(0, 5).map(booking => (
              <div key={booking.id} className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                <div>
                  <p className="font-medium">{booking.customer_name}</p>
                  <p className="text-sm text-zinc-400">{booking.service_name}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm">{formatDateTime(booking.start_time)}</p>
                  <span className={`text-xs px-2 py-1 rounded-full ${statusColors[booking.status]}`}>
                    {booking.status}
                  </span>
                </div>
              </div>
            ))}
            {(!stats?.recent_bookings || stats.recent_bookings.length === 0) && (
              <p className="text-center text-zinc-500 py-8">No bookings yet</p>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-zinc-900 rounded-xl border border-white/10 p-6">
          <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setShowBookingModal(true)}
              className="p-4 bg-zinc-800 hover:bg-zinc-700 rounded-lg flex items-center gap-3 transition-colors"
            >
              <Plus className="w-5 h-5 text-blue-400" />
              <span>New Booking</span>
            </button>
            <button
              onClick={() => setShowServiceModal(true)}
              className="p-4 bg-zinc-800 hover:bg-zinc-700 rounded-lg flex items-center gap-3 transition-colors"
            >
              <Briefcase className="w-5 h-5 text-emerald-400" />
              <span>Add Service</span>
            </button>
            <button
              onClick={() => setShowOperatorModal(true)}
              className="p-4 bg-zinc-800 hover:bg-zinc-700 rounded-lg flex items-center gap-3 transition-colors"
            >
              <User className="w-5 h-5 text-violet-400" />
              <span>Add Staff</span>
            </button>
            <button
              onClick={() => setActiveTab('schedule')}
              className="p-4 bg-zinc-800 hover:bg-zinc-700 rounded-lg flex items-center gap-3 transition-colors"
            >
              <CalendarDays className="w-5 h-5 text-amber-400" />
              <span>View Schedule</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSchedule = () => {
    const today = new Date();
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" className="border-zinc-700">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <h3 className="text-lg font-semibold">{today.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h3>
            <Button variant="outline" size="sm" className="border-zinc-700">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <Button onClick={() => setShowBookingModal(true)} className="bg-blue-500">
            <Plus className="w-4 h-4 mr-2" />New Booking
          </Button>
        </div>

        <div className="bg-zinc-900 rounded-xl border border-white/10 overflow-hidden">
          {/* Calendar Header */}
          <div className="grid grid-cols-7 border-b border-white/10">
            {weekDays.map(day => (
              <div key={day} className="p-4 text-center text-sm font-medium text-zinc-400">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar Grid */}
          <div className="grid grid-cols-7">
            {[...Array(35)].map((_, i) => {
              const dayNum = i - today.getDay() + 1;
              const date = new Date(today.getFullYear(), today.getMonth(), dayNum);
              const isToday = date.toDateString() === today.toDateString();
              const dayBookings = bookings.filter(b => 
                new Date(b.start_time).toDateString() === date.toDateString()
              );
              
              return (
                <div key={i} className={`min-h-[100px] p-2 border-b border-r border-white/5 ${isToday ? 'bg-blue-500/10' : ''}`}>
                  <div className={`text-sm mb-2 ${isToday ? 'text-blue-400 font-bold' : 'text-zinc-400'}`}>
                    {date.getDate()}
                  </div>
                  <div className="space-y-1">
                    {dayBookings.slice(0, 3).map(booking => (
                      <div 
                        key={booking.id} 
                        className="text-xs p-1 rounded bg-blue-500/20 text-blue-300 truncate cursor-pointer hover:bg-blue-500/30"
                        title={`${booking.customer_name} - ${booking.service_name}`}
                      >
                        {new Date(booking.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} - {booking.service_name}
                      </div>
                    ))}
                    {dayBookings.length > 3 && (
                      <div className="text-xs text-zinc-500">+{dayBookings.length - 3} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderBookings = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <select className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white">
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <Button onClick={() => setShowBookingModal(true)} className="bg-blue-500">
          <Plus className="w-4 h-4 mr-2" />New Booking
        </Button>
      </div>

      <div className="bg-zinc-900 rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full">
          <thead className="bg-zinc-800">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Booking</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Service</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Staff</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Date & Time</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Status</th>
              <th className="text-right px-6 py-4 text-sm font-medium text-zinc-400">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {bookings.map(booking => (
              <tr key={booking.id} className="hover:bg-zinc-800/50">
                <td className="px-6 py-4">
                  <p className="font-medium">{booking.customer_name}</p>
                  <p className="text-sm text-zinc-400">{booking.customer_email}</p>
                </td>
                <td className="px-6 py-4">{booking.service_name}</td>
                <td className="px-6 py-4">{booking.operator_name || 'Any available'}</td>
                <td className="px-6 py-4">
                  <p>{formatDateTime(booking.start_time)}</p>
                  <p className="text-sm text-zinc-400">{booking.duration} min</p>
                </td>
                <td className="px-6 py-4">
                  <select
                    value={booking.status}
                    onChange={(e) => updateBookingStatus(booking.id, e.target.value)}
                    className={`px-3 py-1 rounded-full text-xs ${statusColors[booking.status]} bg-transparent border-0 cursor-pointer`}
                  >
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="no_show">No Show</option>
                  </select>
                </td>
                <td className="px-6 py-4 text-right">
                  <button className="p-2 hover:bg-zinc-700 rounded-lg">
                    <Edit2 className="w-4 h-4 text-zinc-400" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {bookings.length === 0 && (
          <div className="text-center py-12 text-zinc-500">
            <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No bookings yet</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderServices = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Services</h3>
        <Button onClick={() => { setEditingItem(null); setServiceForm({ name: '', description: '', duration: 60, price: '', color: '#8b5cf6', is_active: true }); setShowServiceModal(true); }} className="bg-blue-500">
          <Plus className="w-4 h-4 mr-2" />Add Service
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {services.map(service => (
          <div key={service.id} className="bg-zinc-900 rounded-xl border border-white/10 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${service.color}20` }}>
                <Briefcase className="w-5 h-5" style={{ color: service.color }} />
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => {
                  setEditingItem(service);
                  setServiceForm({
                    name: service.name,
                    description: service.description || '',
                    duration: service.duration,
                    price: service.price?.toString() || '',
                    color: service.color,
                    is_active: service.is_active
                  });
                  setShowServiceModal(true);
                }} className="p-1.5 hover:bg-zinc-700 rounded">
                  <Edit2 className="w-4 h-4 text-zinc-400" />
                </button>
                <button onClick={() => deleteService(service.id)} className="p-1.5 hover:bg-red-500/20 rounded">
                  <Trash2 className="w-4 h-4 text-zinc-400 hover:text-red-400" />
                </button>
              </div>
            </div>
            <h4 className="font-semibold mb-2">{service.name}</h4>
            <p className="text-sm text-zinc-400 mb-4 line-clamp-2">{service.description || 'No description'}</p>
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-1 text-zinc-400">
                <Clock className="w-4 h-4" /> {service.duration} min
              </span>
              <span className="font-medium">${service.price?.toFixed(2) || '0.00'}</span>
            </div>
          </div>
        ))}
        {services.length === 0 && (
          <div className="col-span-3 text-center py-12 text-zinc-500 bg-zinc-900 rounded-xl border border-white/10">
            <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No services yet</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderOperators = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Staff Members</h3>
        <Button onClick={() => { setEditingItem(null); setOperatorForm({ name: '', email: '', phone: '', bio: '', services: [], color: '#8b5cf6', is_active: true }); setShowOperatorModal(true); }} className="bg-blue-500">
          <Plus className="w-4 h-4 mr-2" />Add Staff
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {operators.map(operator => (
          <div key={operator.id} className="bg-zinc-900 rounded-xl border border-white/10 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${operator.color}20` }}>
                <span className="text-lg font-bold" style={{ color: operator.color }}>
                  {operator.name?.[0]?.toUpperCase() || 'S'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => {
                  setEditingItem(operator);
                  setOperatorForm({
                    name: operator.name,
                    email: operator.email || '',
                    phone: operator.phone || '',
                    bio: operator.bio || '',
                    services: operator.services || [],
                    color: operator.color,
                    is_active: operator.is_active
                  });
                  setShowOperatorModal(true);
                }} className="p-1.5 hover:bg-zinc-700 rounded">
                  <Edit2 className="w-4 h-4 text-zinc-400" />
                </button>
                <button onClick={() => deleteOperator(operator.id)} className="p-1.5 hover:bg-red-500/20 rounded">
                  <Trash2 className="w-4 h-4 text-zinc-400 hover:text-red-400" />
                </button>
              </div>
            </div>
            <h4 className="font-semibold mb-1">{operator.name}</h4>
            <p className="text-sm text-zinc-400 mb-3">{operator.email}</p>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 rounded-full text-xs ${operator.is_active ? 'bg-green-500/20 text-green-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                {operator.is_active ? 'Active' : 'Inactive'}
              </span>
              <span className="text-xs text-zinc-500">{operator.services?.length || 0} services</span>
            </div>
          </div>
        ))}
        {operators.length === 0 && (
          <div className="col-span-3 text-center py-12 text-zinc-500 bg-zinc-900 rounded-xl border border-white/10">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No staff members yet</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6 max-w-2xl">
      <div className="bg-zinc-900 rounded-xl border border-white/10 p-6">
        <h3 className="text-lg font-semibold mb-6">Booking Settings</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Minimum Advance Booking (hours)</label>
              <input type="number" value={settings.min_advance_booking || 1} onChange={(e) => setSettings({...settings, min_advance_booking: parseInt(e.target.value)})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Maximum Advance Booking (hours)</label>
              <input type="number" value={settings.max_advance_booking || 720} onChange={(e) => setSettings({...settings, max_advance_booking: parseInt(e.target.value)})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Slot Duration (minutes)</label>
              <select value={settings.slot_duration || 30} onChange={(e) => setSettings({...settings, slot_duration: parseInt(e.target.value)})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white">
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={60}>60 minutes</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Cancellation Deadline (hours)</label>
              <input type="number" value={settings.cancellation_deadline || 24} onChange={(e) => setSettings({...settings, cancellation_deadline: parseInt(e.target.value)})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input type="checkbox" id="allow_cancellation" checked={settings.allow_cancellation !== false} onChange={(e) => setSettings({...settings, allow_cancellation: e.target.checked})} className="w-5 h-5 rounded" />
            <label htmlFor="allow_cancellation">Allow customers to cancel bookings</label>
          </div>
          <div className="flex items-center gap-3">
            <input type="checkbox" id="allow_rescheduling" checked={settings.allow_rescheduling !== false} onChange={(e) => setSettings({...settings, allow_rescheduling: e.target.checked})} className="w-5 h-5 rounded" />
            <label htmlFor="allow_rescheduling">Allow customers to reschedule bookings</label>
          </div>
          <div className="flex items-center gap-3">
            <input type="checkbox" id="auto_confirm" checked={settings.auto_confirm !== false} onChange={(e) => setSettings({...settings, auto_confirm: e.target.checked})} className="w-5 h-5 rounded" />
            <label htmlFor="auto_confirm">Auto-confirm bookings</label>
          </div>
          <div className="flex items-center gap-3">
            <input type="checkbox" id="send_reminders" checked={settings.send_reminders !== false} onChange={(e) => setSettings({...settings, send_reminders: e.target.checked})} className="w-5 h-5 rounded" />
            <label htmlFor="send_reminders">Send booking reminders</label>
          </div>
          <div className="pt-4">
            <Button onClick={saveSettings} className="bg-blue-500">
              <Save className="w-4 h-4 mr-2" />Save Settings
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return renderDashboard();
      case 'schedule': return renderSchedule();
      case 'bookings': return renderBookings();
      case 'services': return renderServices();
      case 'operators': return renderOperators();
      case 'settings': return renderSettings();
      default: return renderDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <div className="bg-zinc-900 border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button onClick={() => navigate(`/edit/${websiteId}`)} variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-2" />Back to Editor
          </Button>
          <div className="h-6 w-px bg-zinc-700" />
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-400" />
            <h1 className="text-lg font-semibold text-white">Booking & Appointments</h1>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-zinc-900 border-r border-white/10 min-h-[calc(100vh-73px)]">
          <div className="p-4 space-y-1">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                    activeTab === tab.id 
                      ? 'bg-blue-500/20 text-blue-400' 
                      : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-sm">{tab.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-8">{renderContent()}</div>
      </div>

      {/* Service Modal */}
      {showServiceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="bg-zinc-900 rounded-2xl w-full max-w-lg p-6 border border-white/10">
            <h3 className="text-xl font-bold mb-6">{editingItem ? 'Edit Service' : 'Add Service'}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Service Name *</label>
                <input type="text" value={serviceForm.name} onChange={(e) => setServiceForm({...serviceForm, name: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="Haircut" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea value={serviceForm.description} onChange={(e) => setServiceForm({...serviceForm, description: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white resize-none" rows={3} placeholder="Service description..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Duration (minutes) *</label>
                  <input type="number" value={serviceForm.duration} onChange={(e) => setServiceForm({...serviceForm, duration: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="60" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Price ($)</label>
                  <input type="number" value={serviceForm.price} onChange={(e) => setServiceForm({...serviceForm, price: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="50.00" step="0.01" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Color</label>
                <input type="color" value={serviceForm.color} onChange={(e) => setServiceForm({...serviceForm, color: e.target.value})} className="w-20 h-10 bg-zinc-800 border border-zinc-700 rounded-lg cursor-pointer" />
              </div>
              <div className="pt-4 flex gap-3">
                <Button onClick={() => { setShowServiceModal(false); setEditingItem(null); }} variant="outline" className="flex-1 border-zinc-700">Cancel</Button>
                <Button onClick={saveService} className="flex-1 bg-blue-500">{editingItem ? 'Update' : 'Create'} Service</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Operator Modal */}
      {showOperatorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="bg-zinc-900 rounded-2xl w-full max-w-lg p-6 border border-white/10">
            <h3 className="text-xl font-bold mb-6">{editingItem ? 'Edit Staff' : 'Add Staff'}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Name *</label>
                <input type="text" value={operatorForm.name} onChange={(e) => setOperatorForm({...operatorForm, name: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="John Doe" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <input type="email" value={operatorForm.email} onChange={(e) => setOperatorForm({...operatorForm, email: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="john@example.com" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Phone</label>
                <input type="tel" value={operatorForm.phone} onChange={(e) => setOperatorForm({...operatorForm, phone: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="+1 234 567 890" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Bio</label>
                <textarea value={operatorForm.bio} onChange={(e) => setOperatorForm({...operatorForm, bio: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white resize-none" rows={2} placeholder="Brief bio..." />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Color</label>
                <input type="color" value={operatorForm.color} onChange={(e) => setOperatorForm({...operatorForm, color: e.target.value})} className="w-20 h-10 bg-zinc-800 border border-zinc-700 rounded-lg cursor-pointer" />
              </div>
              <div className="pt-4 flex gap-3">
                <Button onClick={() => { setShowOperatorModal(false); setEditingItem(null); }} variant="outline" className="flex-1 border-zinc-700">Cancel</Button>
                <Button onClick={saveOperator} className="flex-1 bg-blue-500">{editingItem ? 'Update' : 'Add'} Staff</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="bg-zinc-900 rounded-2xl w-full max-w-lg p-6 border border-white/10">
            <h3 className="text-xl font-bold mb-6">New Booking</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Service *</label>
                <select value={bookingForm.service_id} onChange={(e) => setBookingForm({...bookingForm, service_id: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white">
                  <option value="">Select a service</option>
                  {services.map(s => (
                    <option key={s.id} value={s.id}>{s.name} - ${s.price?.toFixed(2)} ({s.duration} min)</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Staff Member</label>
                <select value={bookingForm.operator_id} onChange={(e) => setBookingForm({...bookingForm, operator_id: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white">
                  <option value="">Any available</option>
                  {operators.map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Customer Name *</label>
                <input type="text" value={bookingForm.customer_name} onChange={(e) => setBookingForm({...bookingForm, customer_name: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="John Doe" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Customer Email *</label>
                <input type="email" value={bookingForm.customer_email} onChange={(e) => setBookingForm({...bookingForm, customer_email: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="john@example.com" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Date & Time *</label>
                <input type="datetime-local" value={bookingForm.start_time} onChange={(e) => setBookingForm({...bookingForm, start_time: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Notes</label>
                <textarea value={bookingForm.notes} onChange={(e) => setBookingForm({...bookingForm, notes: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white resize-none" rows={2} placeholder="Any special requests..." />
              </div>
              <div className="pt-4 flex gap-3">
                <Button onClick={() => setShowBookingModal(false)} variant="outline" className="flex-1 border-zinc-700">Cancel</Button>
                <Button onClick={saveBooking} className="flex-1 bg-blue-500">Create Booking</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingPanel;
